import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorFallbackProps {
  /** What failed, in the host's words; never an exception's message. */
  message?: string
  onReset?: () => void
  resetLabel?: string
  /** 'screen' fills the window; 'page' fills its page card, inside the app's frame. */
  layout?: 'screen' | 'page'
}

/**
 * The page that stands in for a crash. It says what happened in plain words;
 * the exception itself (a TypeError's message, a stack) is for the console,
 * where the boundary logs it, not for the person using the app.
 */
export function ErrorFallback({
  message = "We couldn't load this page. You can try again or return to the home page.",
  onReset,
  resetLabel = 'Try again',
  layout = 'screen',
}: ErrorFallbackProps) {
  const handleReload = () => {
    window.location.reload()
  }

  return (
    <div
      className={
        layout === 'page'
          ? 'flex min-h-full flex-1 items-center justify-center p-4'
          : 'flex min-h-screen items-center justify-center bg-default p-4'
      }
    >
      <div className="w-full max-w-md rounded-lg border border-default surface-default p-8 text-center shadow-lg">
        <svg
          className="mx-auto h-12 w-12 text-danger"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        <h1 className="mt-4 text-xl font-semibold text-default">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted">{message}</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          {onReset ? (
            <button
              type="button"
              onClick={onReset}
              className="rounded-md border border-strong surface-default px-4 py-2 text-sm font-medium text-subtle shadow-sm transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
            >
              {resetLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleReload}
            className="rounded-md bg-[color:var(--action-brand-bg)] px-4 py-2 text-sm font-medium text-[color:var(--action-brand-text)] shadow-sm transition-colors hover:bg-[color:var(--action-brand-bg-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  )
}

interface Props {
  children: ReactNode
  fallback?: ReactNode
  /**
   * Wraps the fallback. A boundary outside ThemeController passes one that
   * remounts it, since the crash unmounted the app's (and its cleanup cleared
   * the theme from the document).
   */
  wrapFallback?: (fallback: ReactNode) => ReactNode
  /** Clears a caught error when it changes, e.g. the route path on navigation. */
  resetKey?: unknown
  /** How the default fallback fills its space (see ErrorFallback). */
  fallbackLayout?: 'screen' | 'page'
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console for now; replace with external service later
    console.error('[ErrorBoundary] Uncaught error:', error)
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack)
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.handleReset()
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      const fallback = this.props.fallback ?? (
        <ErrorFallback onReset={this.handleReset} layout={this.props.fallbackLayout} />
      )
      return this.props.wrapFallback ? this.props.wrapFallback(fallback) : fallback
    }

    return this.props.children
  }
}
